import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  InputLabel,
  Alert,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import ListSurgeryUltrasoundReferral from "./ListSurgeryUltrasoundReferral";

const SurgeryUltrasoundReferral = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const { data: ultrasoundTypesData } = useFetchObjects(
    ["ultrasound-types"],
    "ultrasound-types/",
    token
  );
  const { data: surgeries, isLoading: loadingSurgeries } = useFetchObjects(
    ["surgeries"],
    "surgeries/",
    token
  );
  const { data: doctors } = useFetchObjects(["doctors"], "users/", token);

  const { handleAdd, resetForm } = useAdd("surgery-ultrasound-patient", token);

  const ultrasoundTypes = ultrasoundTypesData || [];

  const [ultrasoundDetails, setUltrasoundDetails] = useState({
    ultrasoundType: "",
    testDate: new Date().toISOString().split("T")[0],
    notes: "",
    isUrgent: false,
    totalAmount: "",
    patient: null,
    doctor: null,
  });

  const [errors, setErrors] = useState({});
  const [ultrasoundPercentage, setUltrasoundPercentage] = useState(0);
  const [existingUltrasoundAmount, setExistingUltrasoundAmount] = useState(0);
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [overLimitAmount, setOverLimitAmount] = useState(0);

  useEffect(() => {
    if (resetForm) {
      setUltrasoundDetails({
        ultrasoundType: "",
        testDate: new Date().toISOString().split("T")[0],
        notes: "",
        isUrgent: false,
        totalAmount: "",
        patient: null,
        doctor: null,
      });
      setErrors({});
      setUltrasoundPercentage(0);
      setExistingUltrasoundAmount(0);
      setIsOverLimit(false);
      setOverLimitAmount(0);
    }
  }, [resetForm]);

  useEffect(() => {
    if (ultrasoundDetails.patient && surgeries) {
      const selectedSurgery = surgeries.find(
        (surgery) => surgery.id === ultrasoundDetails.patient
      );
      if (selectedSurgery) {
        setUltrasoundPercentage(selectedSurgery.ultrasound_percentage || 0);
        setExistingUltrasoundAmount(
          selectedSurgery.total_ultrasound_amount || 0
        );
      }
    } else {
      setUltrasoundPercentage(0);
      setExistingUltrasoundAmount(0);
    }
  }, [ultrasoundDetails.patient, surgeries]);

  useEffect(() => {
    const totalCost = parseFloat(ultrasoundDetails.totalAmount || 0);
    const newTotal = existingUltrasoundAmount + totalCost;
    if (newTotal > ultrasoundPercentage) {
      setIsOverLimit(true);
      setOverLimitAmount(newTotal - ultrasoundPercentage);
    } else {
      setIsOverLimit(false);
      setOverLimitAmount(0);
    }
  }, [
    ultrasoundDetails.totalAmount,
    existingUltrasoundAmount,
    ultrasoundPercentage,
  ]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setUltrasoundDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "ultrasoundType") {
      const selectedType = ultrasoundTypes.find(
        (type) => type.id.toString() === value
      );
      setUltrasoundDetails((prev) => ({
        ...prev,
        totalAmount: selectedType ? selectedType.cost.toString() : "",
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    let tempErrors = {};
    if (!ultrasoundDetails.patient) {
      tempErrors.patient = t("doctor.patientRequired");
    }
    if (!ultrasoundDetails.doctor) {
      tempErrors.doctor = t("doctor.doctorRequired");
    }
    if (!ultrasoundDetails.ultrasoundType) {
      tempErrors.ultrasoundType = t("doctor.ultTypeRequired");
    }
    if (!ultrasoundDetails.testDate) {
      tempErrors.testDate = t("doctor.testDateRequired");
    }
    if (!ultrasoundDetails.totalAmount) {
      tempErrors.totalAmount = t("doctor.totalAmountRequired");
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const payload = {
        patient: ultrasoundDetails.patient,
        doctor: ultrasoundDetails.doctor,
        ultrasound_type: ultrasoundDetails.ultrasoundType,
        date: ultrasoundDetails.testDate,
        is_urgent: ultrasoundDetails.isUrgent,
        total_amount: ultrasoundDetails.totalAmount,
        notes: ultrasoundDetails.notes,
      };
      handleAdd(payload);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        margin: "auto",
        width: "100%",
        borderRadius: "16px",
        backgroundColor: "background.default",
        boxShadow: "0px 6px 18px rgba(0,0,0,0.12)",
      }}
    >
      <Typography
        variant="h4"
        textAlign="center"
        fontWeight="bold"
        sx={{ mb: 3 }}
        color="primary"
      >
        {t("doctor.ultReferralForm")}
      </Typography>

      <Divider sx={{ mb: 4 }} />

      <Grid container spacing={3}>
        {/* Patient Selection */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={surgeries || []}
            getOptionLabel={(option) =>
              `${option.patient_display} (ID: ${option.id})`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("doctor.selectSurgery")}
                variant="outlined"
                fullWidth
                error={!!errors.patient}
                helperText={errors.patient}
              />
            )}
            value={
              surgeries?.find(
                (surgery) => surgery.id === ultrasoundDetails.patient
              ) || null
            }
            onChange={(event, newValue) => {
              setUltrasoundDetails((prev) => ({
                ...prev,
                patient: newValue ? newValue.id : null,
              }));
              setErrors((prev) => ({ ...prev, patient: "" }));
            }}
          />
        </Grid>

        {/* Assigned Doctor Selection */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={doctors || []}
            getOptionLabel={(option) =>
              `${option.first_name || t("doctor.unknown")} ${
                option.last_name || ""
              }`.trim()
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("doctor.selectDoctor")}
                variant="outlined"
                fullWidth
                error={!!errors.doctor}
                helperText={errors.doctor}
              />
            )}
            value={
              doctors?.find(
                (doctor) => doctor.id === ultrasoundDetails.doctor
              ) || null
            }
            onChange={(event, newValue) => {
              setUltrasoundDetails((prev) => ({
                ...prev,
                doctor: newValue ? newValue.id : null,
              }));
              setErrors((prev) => ({ ...prev, doctor: "" }));
            }}
          />
        </Grid>

        {/* Ultrasound Type Selection */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t("doctor.ultrasoundType")}</InputLabel>
            <Select
              name="ultrasoundType"
              value={ultrasoundDetails.ultrasoundType}
              onChange={handleChange}
              displayEmpty
              error={!!errors.ultrasoundType}
            >
              {ultrasoundTypes.map((type) => (
                <MenuItem key={type.id} value={type.id.toString()}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            {errors.ultrasoundType && (
              <Typography color="error" variant="caption">
                {errors.ultrasoundType}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Test Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="testDate"
            label={t("doctor.testDate")}
            type="date"
            fullWidth
            value={ultrasoundDetails.testDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            error={!!errors.testDate}
            helperText={errors.testDate}
          />
        </Grid>

        {/* Total Amount */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="totalAmount"
            label={t("doctor.totalAmount")}
            type="number"
            fullWidth
            value={ultrasoundDetails.totalAmount}
            onChange={handleChange}
            error={!!errors.totalAmount}
            helperText={errors.totalAmount}
          />
        </Grid>

        {/* Urgent Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={ultrasoundDetails.isUrgent}
                onChange={handleChange}
                name="isUrgent"
              />
            }
            label={t("doctor.urgent")}
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            name="notes"
            label={t("doctor.notes")}
            multiline
            rows={4}
            fullWidth
            value={ultrasoundDetails.notes}
            onChange={handleChange}
          />
        </Grid>

        {/* Display Warning Message if Over Limit */}
        {isOverLimit && (
          <Grid item xs={12}>
            <Alert severity="warning">
              {t("doctor.totalCostExceedsUltrasoundLimit", {
                high_amount:
                  existingUltrasoundAmount +
                  parseFloat(ultrasoundDetails.totalAmount || 0),
                ultrasound_percentage: ultrasoundPercentage,
              })}
            </Alert>
          </Grid>
        )}

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
          >
            {t("doctor.submit")}
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ mt: 4 }} />

      <Box sx={{ mt: 3 }}>
        <ListSurgeryUltrasoundReferral />
      </Box>
    </Paper>
  );
};

export default SurgeryUltrasoundReferral;
